import { useState, useRef, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/store/cart';
import { formatPrice } from '@/lib/shopify';
import { haptic } from '@/hooks/useHaptics';
import { Link } from 'react-router-dom';
import CartBundleProgress from '@/components/CartBundleProgress';
import { calcBundleDiscount } from '@/lib/bundleDeals';
import BrandWord from '@/components/BrandWord';
import { useLenisLock } from '@/hooks/useLenisLock';


export default function CartDrawer() {
  const { t, i18n } = useTranslation();
  const { items, isOpen, isCheckingOut, setOpen, removeItem, updateQuantity, totalPrice, checkout } = useCartStore();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [shakeTerms, setShakeTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const termsRef = useRef<HTMLLabelElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useLenisLock(isOpen);

  const handleCheckoutClick = () => {
    if (!agreedToTerms) {
      setShakeTerms(false);
      setShowTermsError(true);
      requestAnimationFrame(() => setShakeTerms(true));
      termsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      haptic('warn');
      return;
    }
    haptic('success');
    checkout();
  };

  // Body scroll lock + ESC to close.
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, setOpen]);

  // Swipe-down to close on mobile (touch on the handle area)
  const touchStartY = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (dy > 70) setOpen(false);
    touchStartY.current = null;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t('common.close', 'Close')}
        className="fixed inset-0 bg-black/45 z-50 backdrop-blur-sm animate-fade-in"
        onClick={() => setOpen(false)}
      />

      {/* Drawer — side panel on md+, bottom sheet on mobile */}
      <div
        ref={panelRef}
        data-lenis-prevent
        role="dialog"
        aria-modal="true"
        aria-label={t('cart.title', 'Your Cart')}
        className={[
          'fixed z-50 bg-background shadow-2xl grid min-h-0 overflow-hidden',
          // Mobile: bottom sheet
          'inset-x-0 bottom-0 h-[92dvh] grid-rows-[auto,minmax(0,1fr),auto] rounded-t-2xl animate-slide-up-sheet',
          // Desktop: side drawer
          'md:inset-y-0 md:end-0 md:bottom-auto md:start-auto md:h-dvh md:w-full md:max-w-md md:grid-rows-[auto,minmax(0,1fr),auto] md:rounded-none md:animate-slide-in-side',
        ].join(' ')}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Mobile swipe-handle */}
        <div
          className="md:hidden pt-2 pb-1 flex justify-center cursor-grab active:cursor-grabbing"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          aria-hidden="true"
        >
          <span className="h-1.5 w-12 rounded-full bg-foreground/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 md:px-6 py-3 md:py-4 border-b border-border/50">
          <h2 className="font-display text-lg text-foreground flex items-baseline gap-2">
            <BrandWord prefix="BUX" suffix="BAG" className="text-base md:text-lg" />
            {items.length > 0 && (
              <span className="font-body text-xs text-muted-foreground">
                ({items.reduce((n, i) => n + i.quantity, 0)})
              </span>
            )}
          </h2>
          <button
            onClick={() => setOpen(false)}
            className="min-w-[2.75rem] min-h-[2.75rem] -mr-2 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Items — overscroll-contain so iOS doesn't drag the page underneath */}
        <div
          data-lenis-prevent
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 md:px-6 py-4 touch-pan-y"
          onWheelCapture={(event) => event.stopPropagation()}
          onTouchMoveCapture={(event) => event.stopPropagation()}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-sm text-muted-foreground mb-6">
                {t('cart.empty', 'Your cart is empty')}
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-block font-body text-[0.7rem] tracking-[0.3em] uppercase text-foreground border-b border-foreground/60 hover:border-foreground pb-1"
              >
                {t('cart.continueShopping', 'Continue Shopping')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-5 md:gap-6">
              {items.map((item, idx) => (
                <div
                  key={item.variantId}
                  className="flex gap-3 md:gap-4 cart-item-in"
                  style={{ animationDelay: `${Math.min(idx, 6) * 40}ms` }}
                >
                  {item.image && (
                    <img
                      src={item.image} alt="" role="presentation"
                      className="w-24 h-28 md:w-28 md:h-32 object-cover rounded-lg bg-muted/30 shrink-0"
                      width={112} height={128}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-body text-sm font-medium text-foreground line-clamp-2">{item.title}</h3>
                    <p className="font-body text-xs text-muted-foreground mb-2 truncate">{item.variantTitle}</p>
                    <p className="font-body text-base text-pastel font-light tracking-tight mb-2">{formatPrice(item.price, item.currencyCode, i18n.language)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { haptic('soft'); updateQuantity(item.variantId, item.quantity - 1); }}
                        className="tap-press w-9 h-9 flex items-center justify-center border border-border/60 rounded-full text-base hover:bg-muted/50 transition-colors"
                        aria-label={t('product.decrease', 'Decrease')}
                      >−</button>
                      <span className="font-body text-sm w-6 text-center" aria-live="polite">{item.quantity}</span>
                      <button
                        onClick={() => { haptic('soft'); updateQuantity(item.variantId, item.quantity + 1); }}
                        className="tap-press w-9 h-9 flex items-center justify-center border border-border/60 rounded-full text-base hover:bg-muted/50 transition-colors"
                        aria-label={t('product.increase', 'Increase')}
                      >+</button>
                      <button
                        onClick={() => { haptic('warn'); removeItem(item.variantId); }}
                        className="tap-press ms-auto text-muted-foreground hover:text-destructive transition-colors min-w-[2.75rem] min-h-[2.75rem] flex items-center justify-center"
                        aria-label={t('cart.remove', 'Remove')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sticky bottom block: bundle progress + footer always visible */}
        {items.length > 0 && (
          <div className="min-h-0 bg-background">
            <CartBundleProgress onClose={() => setOpen(false)} />
            <div className="px-5 md:px-6 pt-3 pb-4 border-t border-border/50 bg-background">
            {(() => {
              const subtotal = totalPrice();
              const bundleDiscount = calcBundleDiscount(items);
              const finalTotal = Math.max(0, subtotal - bundleDiscount);
              const currency = items[0]?.currencyCode ?? 'EUR';
              return (
                <>
                  {bundleDiscount > 0.005 && (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-body text-xs text-muted-foreground">{t('cart.subtotal', 'Subtotal')}</span>
                        <span className="font-body text-sm text-muted-foreground line-through">{formatPrice(subtotal, currency, i18n.language)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-body text-xs uppercase tracking-[0.2em] text-accent">{t('cart.bundleSavings', 'Bundle savings')}</span>
                        <span className="font-body text-sm text-accent">−{formatPrice(bundleDiscount, currency, i18n.language)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="font-body text-sm text-muted-foreground">{t('cart.total', 'Total')}</span>
                    <span className="font-body text-xl md:text-2xl font-light tracking-tight text-pastel">{formatPrice(finalTotal, currency, i18n.language)}</span>
                  </div>
                </>
              );
            })()}

            <label
              ref={termsRef}
              onAnimationEnd={() => setShakeTerms(false)}
              className={`flex items-start gap-3 mb-2.5 cursor-pointer select-none rounded-lg p-2 -m-2 transition-colors ${
                shakeTerms ? 'animate-shake bg-destructive/10 ring-1 ring-destructive/40' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked) setShowTermsError(false);
                }}
                className={`mt-0.5 h-4 w-4 shrink-0 rounded border accent-foreground ${
                  shakeTerms ? 'border-destructive' : 'border-border'
                }`}
              />
              <span className={`font-body text-xs leading-relaxed ${shakeTerms ? 'text-destructive' : 'text-muted-foreground'}`}>
                <Trans
                  i18nKey="cart.agreeTerms"
                  components={{
                    termsLink: <Link to="/terms" onClick={() => setOpen(false)} className="underline text-foreground hover:text-foreground/80" />,
                    privacyLink: <Link to="/privacy" onClick={() => setOpen(false)} className="underline text-foreground hover:text-foreground/80" />,
                  }}
                />
              </span>
            </label>

            {showTermsError && (
              <p className="mb-2 font-body text-xs text-destructive" role="alert">
                {t('cart.termsRequired', 'Please agree to the terms to continue.')}
              </p>
            )}

            <Button
              onClick={handleCheckoutClick}
              disabled={isCheckingOut}
              className="tap-press w-full rounded-full min-h-[3rem] font-body text-sm tracking-widest uppercase bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
            >
              {isCheckingOut
                ? t('cart.processing', 'Processing…')
                : t('cart.checkout', 'Checkout')
              }
            </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
